import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { ClickResponse } from './click.response.model';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from 'src/environments/environment';

@Injectable()
export class AuthService {
    token: string;
    private loggedUserEmail: string = null;
    private loggedUser: any = null;
    private impersonatedUser: any = null;
    private vendorInfo = undefined;
    private vendorInfoPromise = undefined;
    private appToken = null;

    constructor(private router: Router, private httpClient: HttpClient, private route: ActivatedRoute) {
        const navigation = this.router.getCurrentNavigation();
        this.appToken = navigation.extras.state && navigation.extras.state.token ? navigation.extras.state.token : null;
    }

    get vendorTokenInfo() {
        if (!this.vendorInfoPromise) {
            if (!this.appToken) {
                this.appToken = this.route.snapshot.queryParams.token;
            }
            const url = environment.gateway + `vendor/info?token=${this.appToken}`;
            const httpOptions = {
                headers: this.getHttpHeadders('application/json')
            };

            this.vendorInfoPromise = new Promise((resolve) => {
                if (this.vendorInfo != undefined) {
                    resolve(this.vendorInfo);
                } else if (this.route.snapshot.queryParams.token) {
                    this.httpClient.post(url, httpOptions).pipe(tap(
                        (data: any) => {
                            this.vendorInfo = data.object;
                            resolve(this.vendorInfo);
                        },
                        () => {
                            this.vendorInfo = {};
                            resolve(this.vendorInfo);
                        }
                    )).subscribe();
                } else {
                    this.vendorInfo = {};
                    resolve(this.vendorInfo);
                }
            });
        }

        return this.vendorInfoPromise;
    }

    singinUser(email: string, password: string) {
        return new Promise((resolve, reject) => {
            const url = environment.gateway + 'oauth/token/';
            const postData = new HttpParams()
                .set('grant_type', 'password')
                .set('username', email)
                .set('password', encodeURI(btoa(password)));
            const httpOptions = {
                headers: this.getHttpHeadders('application/x-www-form-urlencoded')
            };
            this.httpClient.post<ClickResponse>(url, postData.toString(), httpOptions).subscribe(
                (data: any) => {
                    localStorage.setItem('click_oauth', JSON.stringify(data.object));
                    this.loggedUserEmail = email;
                    resolve(data);
                },
                (error: any) => {
                    this.loggedUserEmail = null;
                    reject(error);
                }
            );
        });
    }

    getLoggedUserData() {
        const url = environment.gateway + 'user/' + this.loggedUserEmail + '?access_token=' + this.getAccessToken();
        const httpOptions = {
            headers: this.getHttpHeadders('application/json')
        };

        return new Promise((resolve, reject) => {
            this.httpClient.get<ClickResponse>(url, httpOptions).subscribe(
                (data: any) => {
                    this.loggedUser = data.object;
                    localStorage.setItem('logged_user', JSON.stringify(data.object));
                    localStorage.removeItem('impersonated_user');
                    this.impersonatedUser = null;
                    resolve(data);

                },
                (error: any) => {
                    this.loggedUser = null;
                    this.impersonatedUser = null;
                    reject(error);
                }
            );
        });
    }

    getVendors() {
        const user = JSON.parse(localStorage.getItem('logged_user'));
        if (!user) { return null; }
        const broker = user.defaultVendor;
        const vendors = user.salesPersonList;
        let salesPerList = [];
        for (let vend = 0; vend < vendors.length + 1; vend++) {
            if (vend != 0) {
                salesPerList[vend] = vendors[vend - 1]

            } else {
                salesPerList[vend] = broker;

            }
        }
        return salesPerList;
    }

    sendResetPasswordEmail(email: string) {
        const url = environment.gateway + 'user/resetpassword/sendToken?email=' + email;
        const httpOptions = {
            headers: this.getHttpHeadders('application/json')
        };

        return this.httpClient.post(url, httpOptions);
    }

    resetPassword(token: string, password: string) {
        const url = environment.gateway + 'user/resetpassword/reset?token=' + token + '&password=' + encodeURI(btoa(password));
        const httpOptions = {
            headers: this.getHttpHeadders('application/json')
        };

        return new Promise((resolve, reject) => {
            this.httpClient.post(url, httpOptions).pipe(tap(
                data => resolve(data),
                error => reject(error)
            )).subscribe();
        });
    }

    signOutUser() {
        this.token = '';
        localStorage.removeItem('click_oauth');
        localStorage.removeItem('logged_user');
        localStorage.removeItem('impersonated_user');
        localStorage.removeItem('vendSelected');
        this.router.navigate(['signin']);
        this.loggedUserEmail = null;
        this.loggedUser = null;
        this.impersonatedUser = null;
    }


    getAccessToken() {
        const click_oauth = JSON.parse(localStorage.getItem('click_oauth'));
        return click_oauth != null ? JSON.parse(localStorage.getItem('click_oauth')).access_token : null;
    }

    getLoggedUser() {
        if (!this.loggedUser) {
            this.loggedUser = JSON.parse(localStorage.getItem('logged_user'));
        }
        return this.loggedUser;
    }

    get maxApplicationAmount() {
        return new Promise((resolve) => {
            this.vendorTokenInfo.then(
                (data: any) => {
                    if (data.maxApplicationAmount) {
                        resolve(data.maxApplicationAmount);
                    } else if (this.route.snapshot.queryParams.token) {
                        resolve(null);
                    } else {
                        resolve(this.vendor.maxApplicationAmount);
                    }
                }
            );
        });
    }

    getImpersonatedUser() {
        if (!this.impersonatedUser) {
            this.impersonatedUser = JSON.parse(localStorage.getItem('impersonated_user'));
        }
        return this.impersonatedUser;
    }

    vendorSelected() {
        const loggedUser = JSON.parse(localStorage.getItem('vendSelected'));
        if (loggedUser) {
            return loggedUser
        }
    }

    getUserId() {
        return this.getLoggedUser() ? this.loggedUser.id : null;
    }

    getSalesPersonId() {
        return this.vendor.salesPersonId ? this.vendor.salesPersonId : this.vendor.salespersonId;
    }

    getVendorId() {
        return this.vendor.vendorId;
    }

    getVendorType() {
        return this.vendor.vendorType;
    }

    get vendor() {
        let vendor: any;
        if (this.getImpersonatedUser()) {
            return this.impersonatedUser;
        } else {
            if (this.vendorSelected()) {
                vendor = this.vendorSelected()
                return vendor;
            } else {
                return this.getLoggedUser() ? this.loggedUser.defaultVendor : {};
            }
        }
    }

    getEmail() {
        if (this.getImpersonatedUser()) {
            return this.impersonatedUser.userName;
        } else {
            return this.getLoggedUser() ? this.loggedUser.userName : null;
        }
    }

    getMaxApplicationAmount() {
        if (this.getImpersonatedUser()) {
            return this.impersonatedUser.maxApplicationAmount;
        } else {
            return this.getLoggedUser() ? this.loggedUser.defaultVendor.maxApplicationAmount : null;
        }
    }

    setImpersonatedVendor(impersonatedUser: any) {
        this.impersonatedUser = impersonatedUser;
        localStorage.setItem('impersonated_user', JSON.stringify(impersonatedUser));
    }

    clearImpersonatedVendor() {
        this.impersonatedUser = null;
        localStorage.removeItem('impersonated_user');
    }

    validateSession() {
        const url = environment.gateway + 'oauth/validateToken?access_token=' + this.getAccessToken();
        const httpOptions = {
            headers: this.getHttpHeadders('application/json')
        };

        return new Promise((resolve, reject) => {
            this.httpClient.get(url, httpOptions).pipe(tap(
                data => resolve(data),
                error => reject(error)
            )).subscribe();
        });
    }

    validateToken(token: string) {
        const url = environment.gateway + 'validateToken?token=' + token;
        const httpOptions = {
            headers: this.getHttpHeadders('application/json')
        };

        return new Promise((resolve, reject) => {
            this.httpClient.post(url, httpOptions).pipe(tap(
                data => resolve(data),
                error => reject(error)
            )).subscribe();
        });
    }

    private getHttpHeadders(pContentType: string): HttpHeaders {
        if (environment.userSecret && environment.x3scaleProxySecretToken && environment.xClkToken) {
            return new HttpHeaders({
                'Content-type': pContentType,
                'user-secret': environment.userSecret,
                'X-3scale-proxy-secret-token': environment.x3scaleProxySecretToken,
                'X-clk-token': environment.xClkToken,
                'X-application-source': environment.xApplicationSource
            });
        } else {
            return new HttpHeaders({
                'Content-type': pContentType,
                'X-application-source': environment.xApplicationSource
            });
        }
    }
}
